import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import LandingPage from "@/components/LandingPage";
import AuthPage from "@/components/AuthPage";
import Dashboard from "@/components/Dashboard";
import GeneratePage from "@/components/GeneratePage";
import HistoryPage from "@/components/HistoryPage";
import PricingPage from "@/components/PricingPage";
import CalendarPage from "@/components/CalendarPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
const apiClient = axios.create({ baseURL: API });

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && error.response?.data?.detail === "Token expired") {
      localStorage.removeItem("cf_token");
      window.location.href = "/auth";
    }
    return Promise.reject(error);
  }
);

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("cf_token"));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const logout = useCallback(() => {
    localStorage.removeItem("cf_token");
    setToken(null);
    setUser(null);
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    if (token) {
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      apiClient.get("/auth/me").then(res => {
        setUser(res.data.user);
        setLoading(false);
      }).catch(() => {
        logout();
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token, logout]);

  const handleAuth = (authToken, authUser) => {
    localStorage.setItem("cf_token", authToken);
    setToken(authToken);
    setUser(authUser);
    apiClient.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
    navigate("/dashboard");
  };

  const refreshUser = async () => {
    try {
      const res = await apiClient.get("/auth/me");
      setUser(res.data.user);
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
      <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <AuthPage onAuth={handleAuth} apiClient={apiClient} />} />
      <Route path="/dashboard" element={user ? <Dashboard user={user} apiClient={apiClient} onLogout={logout} refreshUser={refreshUser} /> : <Navigate to="/auth" />} />
      <Route path="/generate" element={user ? <GeneratePage user={user} apiClient={apiClient} onLogout={logout} refreshUser={refreshUser} /> : <Navigate to="/auth" />} />
      <Route path="/history" element={user ? <HistoryPage user={user} apiClient={apiClient} onLogout={logout} /> : <Navigate to="/auth" />} />
      <Route path="/calendar" element={user ? <CalendarPage user={user} apiClient={apiClient} onLogout={logout} /> : <Navigate to="/auth" />} />
      <Route path="/pricing" element={<PricingPage user={user} apiClient={apiClient} onLogout={logout} />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
